import { useState, useEffect } from 'react';
import axios from 'axios';
import { Calendar, Plus, Trash2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Checkbox } from '@/components/ui/checkbox';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CustomerCalendarMaster = () => {
  const [calendars, setCalendars] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [newEntry, setNewEntry] = useState({
    customer_id: '',
    date: '',
    is_holiday: true,
    holiday_name: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedCustomer) {
      fetchCustomerCalendar(selectedCustomer);
    }
  }, [selectedCustomer]);

  const fetchData = async () => {
    try {
      const custRes = await axios.get(`${API}/customers`);
      setCustomers(custRes.data);
      if (custRes.data.length > 0) {
        setSelectedCustomer(custRes.data[0].id);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const fetchCustomerCalendar = async (customerId) => {
    try {
      const response = await axios.get(`${API}/customer-calendar/${customerId}`);
      setCalendars(response.data.filter(c => c.is_holiday));
    } catch (error) {
      console.error('Error fetching calendar:', error);
    }
  };

  const handleCreate = async () => {
    if (!newEntry.customer_id || !newEntry.date) {
      toast.error('Customer and date are required');
      return;
    }

    try {
      await axios.post(`${API}/customer-calendar`, {
        ...newEntry,
        is_holiday: true
      });
      toast.success('Holiday added to customer calendar');
      setNewEntry({ customer_id: '', date: '', is_holiday: true, holiday_name: '' });
      if (selectedCustomer) {
        fetchCustomerCalendar(selectedCustomer);
      }
    } catch (error) {
      toast.error('Failed to add holiday');
    }
  };

  const handleDelete = async (calendarId) => {
    try {
      await axios.delete(`${API}/customer-calendar/${calendarId}`);
      toast.success('Holiday deleted');
      if (selectedCustomer) {
        fetchCustomerCalendar(selectedCustomer);
      }
    } catch (error) {
      toast.error('Failed to delete holiday');
    }
  };

  const handleBulkAddSundays = async () => {
    if (!selectedCustomer) {
      toast.error('Select a customer first');
      return;
    }

    try {
      // Get next 90 days of Sundays
      const sundays = [];
      const today = new Date();
      for (let i = 0; i < 90; i++) {
        const date = new Date(today);
        date.setDate(today.getDate() + i);
        if (date.getDay() === 0) { // Sunday
          sundays.push({
            customer_id: selectedCustomer,
            date: date.toISOString().split('T')[0],
            is_holiday: true,
            holiday_name: 'Sunday'
          });
        }
      }

      await axios.post(`${API}/customer-calendar/bulk`, sundays);
      toast.success(`${sundays.length} Sundays added to calendar`);
      fetchCustomerCalendar(selectedCustomer);
    } catch (error) {
      toast.error('Failed to add Sundays');
    }
  };

  const getCustomerName = (id) => customers.find(c => c.id === id)?.name || id;

  return (
    <div className="space-y-6">
      <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-orange-600 mt-0.5" />
        <div>
          <p className="font-medium text-orange-900">Customer Working Calendar Management</p>
          <p className="text-sm text-orange-700 mt-1">
            Mark holidays (Sundays, Diwali, festival days) so daily demand split excludes these dates.
            Example: If Oct 21-22 are holidays, the monthly demand will be distributed only across working days.
          </p>
        </div>
      </div>

      <Card data-testid="calendar-create-card">
        <CardHeader>
          <CardTitle>Add Holiday / Non-Working Day</CardTitle>
          <CardDescription>Mark specific dates as holidays for demand planning</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Customer</label>
              <Select
                data-testid="select-customer"
                value={newEntry.customer_id}
                onValueChange={(value) => setNewEntry({ ...newEntry, customer_id: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select customer" />
                </SelectTrigger>
                <SelectContent>
                  {customers.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Date</label>
              <Input
                data-testid="input-date"
                type="date"
                value={newEntry.date}
                onChange={(e) => setNewEntry({ ...newEntry, date: e.target.value })}
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Holiday Name</label>
              <Input
                data-testid="input-holiday-name"
                placeholder="e.g., Diwali, Sunday"
                value={newEntry.holiday_name}
                onChange={(e) => setNewEntry({ ...newEntry, holiday_name: e.target.value })}
              />
            </div>

            <div className="flex items-end">
              <Button data-testid="add-holiday-btn" onClick={handleCreate} className="w-full">
                <Plus className="w-4 h-4 mr-2" />
                Add Holiday
              </Button>
            </div>
          </div>

          <div className="flex gap-2">
            <Button variant="outline" onClick={handleBulkAddSundays} data-testid="add-sundays-btn">
              <Calendar className="w-4 h-4 mr-2" />
              Auto-Add Sundays (Next 90 Days)
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card data-testid="calendar-list-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Customer Working Calendar - Holidays</CardTitle>
            <div>
              <label className="text-sm font-medium mr-2">View Calendar For:</label>
              <Select value={selectedCustomer} onValueChange={setSelectedCustomer}>
                <SelectTrigger className="w-64">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {customers.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <CardDescription>
            Marked holidays for {getCustomerName(selectedCustomer)} - Demand will not be split on these dates
          </CardDescription>
        </CardHeader>
        <CardContent>
          {calendars.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              No holidays marked. Add holidays above or auto-add Sundays.
            </div>
          ) : (
            <div className="overflow-auto max-h-[400px]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Day</TableHead>
                    <TableHead>Holiday Name</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {calendars
                    .sort((a, b) => new Date(a.date) - new Date(b.date))
                    .map((cal) => {
                      const date = new Date(cal.date);
                      const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
                      
                      return (
                        <TableRow key={cal.id} data-testid={`calendar-row-${cal.id}`}>
                          <TableCell className="font-medium">{cal.date}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{dayName}</Badge>
                          </TableCell>
                          <TableCell>{cal.holiday_name || 'Holiday'}</TableCell>
                          <TableCell>{getCustomerName(cal.customer_id)}</TableCell>
                          <TableCell className="text-right">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDelete(cal.id)}
                              data-testid={`delete-cal-${cal.id}`}
                            >
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CustomerCalendarMaster;
